// Sign-in and first-run setup screens, shown before the app loads any data.
import { Api } from "../../platform/api.js";
import { LangToggle, useT } from "../i18n.jsx";
import { Logo, T } from "../theme.jsx";
import { Button, Field, Notice } from "./ui.jsx";

const { useState } = React;

function AuthFrame({ config, title, subtitle, children }) {
  const { t } = useT();
  React.useEffect(() => { document.title = `${title} · ${t("venueName")} — Rundown`; });
  return (
    <main style={{ minHeight: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ width: "min(420px, 100%)", background: T.panel, border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Logo size={38} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 900, color: T.text }}>{t("venueName")}</div>
              <div style={{ fontSize: 12.5, color: T.subtext }}>{(config && config.orgName) || t("venueSubtitle")}</div>
            </div>
          </div>
          <div style={{ background: T.dark, borderRadius: 8, padding: 2 }}><LangToggle compact /></div>
        </div>
        <h1 style={{ fontSize: 21, fontWeight: 900, color: T.text, margin: "0 0 4px" }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13.5, color: T.subtext, margin: "0 0 16px", lineHeight: 1.5 }}>{subtitle}</p>}
        {children}
      </div>
    </main>
  );
}

function authErrorText(code, t, fallback) {
  const key = `authErr_${code}`;
  const txt = t(key);
  return txt !== key ? txt : (fallback || t("authErr_generic"));
}

// Sign in / sign up, as two tabs on one card. Returning people are normally
// never here: "remember me" keeps them signed in on that browser.
function LoginScreen({ config, initialError, onSignedIn }) {
  const { t } = useT();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState("");
  const canSignUp = !!(config.local && config.signup);
  const tabs = [["signin", t("signInTitle")], ["signup", t("signUpTab")]];
  const pick = (m) => { setMode(m); setNotice(""); };
  return (
    <AuthFrame config={config} title={mode === "signup" ? t("signUpTitle") : t("signInTitle")} subtitle={mode === "signup" ? t("signUpSubtitle") : canSignUp ? t("signInSubtitleSignup") : t("signInSubtitle")}>
      {canSignUp && (
        <div role="tablist" aria-label={t("authTabsLabel")} style={{ display: "flex", gap: 4, background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 10, padding: 4, marginBottom: 16 }}>
          {tabs.map(([m, label]) => (
            <button key={m} type="button" role="tab" id={`auth-tab-${m}`} aria-selected={mode === m} aria-controls={`auth-panel-${m}`} className="rb-btn"
              onClick={() => pick(m)}
              style={{ flex: 1, border: "none", borderRadius: 7, padding: "9px 10px", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
                background: mode === m ? T.accent : "transparent", color: mode === m ? T.accentText : T.text }}>
              {label}
            </button>
          ))}
        </div>
      )}
      <div role={canSignUp ? "tabpanel" : undefined} id={`auth-panel-${mode}`} aria-labelledby={canSignUp ? `auth-tab-${mode}` : undefined}>
        {notice && <Notice tone="ok">{notice}</Notice>}
        {mode === "signup" && canSignUp
          ? <SignUpForm config={config} onDone={(em) => { setEmail(em); setMode("signin"); setNotice(t("signUpDone")); }} />
          : <SignInForm config={config} initialError={initialError} email={email} setEmail={setEmail} onSignedIn={onSignedIn} onAttempt={() => setNotice("")} />}
      </div>
    </AuthFrame>
  );
}

function SignInForm({ config, initialError, email, setEmail, onSignedIn, onAttempt }) {
  const { t } = useT();
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError || "");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); onAttempt();
    try {
      await Api.login(email, password, remember);
      await onSignedIn();
    } catch (err) {
      setError(err.code === "too_many_attempts" ? err.message : authErrorText(err.code, t, err.message));
      setBusy(false);
    }
  };
  return (
    <>
      {error && <Notice>{error}</Notice>}
      {config.google && (
        <>
          <a className="rb-btn" href={Api.googleStartUrl()}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", boxSizing: "border-box", padding: "11px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: "#fff", color: T.text, fontSize: 14, fontWeight: 700, textDecoration: "none" }}>
            <span aria-hidden="true" style={{ fontWeight: 900, color: "#4285F4" }}>G</span> {t("signInWithGoogle")}
          </a>
          {config.local && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0", color: T.subtext, fontSize: 12.5 }}>
              <div style={{ flex: 1, height: 1, background: T.border }} />{t("orDivider")}<div style={{ flex: 1, height: 1, background: T.border }} />
            </div>
          )}
        </>
      )}
      {config.local && (
        <form onSubmit={submit} noValidate>
          <Field label={t("emailLabel")} inputProps={{ type: "email", autoComplete: "username", value: email, onChange: (e) => setEmail(e.target.value), required: true }} />
          <Field label={t("passwordLabel")} inputProps={{ type: "password", autoComplete: "current-password", value: password, onChange: (e) => setPassword(e.target.value), required: true }} />
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: T.text, margin: "2px 0 14px", cursor: "pointer" }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ width: 17, height: 17, accentColor: T.accent }} />
            {t("rememberMe").replace("{days}", String(config.rememberDays || 30))}
          </label>
          <Button type="submit" disabled={busy || !email || !password} style={{ width: "100%", padding: "11px 14px", fontSize: 14 }}>
            {busy ? t("signingIn") : t("signInBtn")}
          </Button>
        </form>
      )}
    </>
  );
}

function SignUpForm({ config, onDone }) {
  const { t } = useT();
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const domains = (config.signupDomains || []).map((d) => "@" + d).join(", ");
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) { setError(t("passwordsDontMatch")); return; }
    setBusy(true); setError("");
    try {
      await Api.signup({ name: f.name, email: f.email, password: f.password });
      onDone(f.email.trim());
    } catch (err) {
      if (err.code === "signup_domain" && domains) setError(t("signUpEmailHint").replace("{domains}", domains));
      else setError(err.code === "too_many_attempts" ? err.message : authErrorText(err.code, t, err.message));
      setBusy(false);
    }
  };
  return (
    <>
      {error && <Notice>{error}</Notice>}
      <form onSubmit={submit} noValidate>
        <Field label={t("nameLabel")} inputProps={{ value: f.name, onChange: set("name"), autoComplete: "name" }} />
        <Field label={t("emailLabel")} hint={domains ? t("signUpEmailHint").replace("{domains}", domains) : undefined} inputProps={{ type: "email", value: f.email, onChange: set("email"), autoComplete: "username" }} />
        <Field label={t("passwordLabel")} hint={t("passwordHint")} inputProps={{ type: "password", value: f.password, onChange: set("password"), autoComplete: "new-password" }} />
        <Field label={t("confirmPasswordLabel")} inputProps={{ type: "password", value: f.confirm, onChange: set("confirm"), autoComplete: "new-password" }} />
        <Button type="submit" disabled={busy || !f.email || !f.password || !f.confirm} style={{ width: "100%", padding: "11px 14px", fontSize: 14 }}>
          {busy ? t("signingUp") : t("signUpBtn")}
        </Button>
      </form>
    </>
  );
}

function SetupScreen({ config, onSignedIn }) {
  const { t } = useT();
  const [f, setF] = useState({ name: "", email: "", password: "", confirm: "", setupToken: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) { setError(t("passwordsDontMatch")); return; }
    setBusy(true); setError("");
    try {
      await Api.setup({ name: f.name, email: f.email, password: f.password, setupToken: f.setupToken });
      await onSignedIn();
    } catch (err) {
      setError(err.message || t("authErr_generic"));
      setBusy(false);
    }
  };
  return (
    <AuthFrame config={config} title={t("setupTitle")} subtitle={t("setupDesc")}>
      {error && <Notice>{error}</Notice>}
      <form onSubmit={submit} noValidate>
        <Field label={t("nameLabel")} inputProps={{ value: f.name, onChange: set("name"), autoComplete: "name" }} />
        <Field label={t("emailLabel")} inputProps={{ type: "email", value: f.email, onChange: set("email"), autoComplete: "username" }} />
        <Field label={t("passwordLabel")} hint={t("passwordHint")} inputProps={{ type: "password", value: f.password, onChange: set("password"), autoComplete: "new-password" }} />
        <Field label={t("confirmPasswordLabel")} inputProps={{ type: "password", value: f.confirm, onChange: set("confirm"), autoComplete: "new-password" }} />
        <Field label={t("setupCodeLabel")} hint={t("setupCodeHint")} inputProps={{ value: f.setupToken, onChange: set("setupToken"), autoComplete: "off" }} />
        <Button type="submit" disabled={busy || !f.email || !f.password} style={{ width: "100%", padding: "11px 14px", fontSize: 14 }}>
          {busy ? t("signingIn") : t("createAdminBtn")}
        </Button>
      </form>
    </AuthFrame>
  );
}

export { LoginScreen, SetupScreen, authErrorText };
